import Typography from "aldoo-ra/Typography"
import PathCell from "./path-cell"

// Enhanced name detection function
const getItemName = (item) => {
  // For CMS content, prioritize content.type or content.label
  if (item.content) {
    return item.content.type || item.content.label || item.content.name || item.content.title
  }
  // For regular items
  return item.name || item.label || item.title || item.type || "Unnamed"
}

// Enhanced description detection function
const getItemDescription = (item) => {
  // For CMS content, use content.label if it exists and is different from type
  if (item.content) {
    if (item.content.label && item.content.label !== item.content.type) {
      return item.content.label
    }
    return item.content.description || ""
  }
  // For regular items
  return item.description || item.label || ""
}

// Enhanced ID detection function
const getItemId = (item) => {
  // For CMS content, try content.id first
  if (item.content) {
    return item.content.id || item._id || "No ID"
  }
  return item.id || item._id || "No ID"
}

// Base columns that are common across most types
const baseColumns = [
  {
    id: "name",
    title: "Name",
    headerClassName: "w-[250px]",
    render: (value, item) => (
      <div className="flex flex-col">
        <Typography className="text-admin_text font-bold">
          {getItemName(item)}
        </Typography>
        {getItemDescription(item) && (
          <Typography className="text-admin_text_accent text-sm opacity-70">
            {getItemDescription(item)}
          </Typography>
        )}
      </div>
    ),
  },
  {
    id: "id",
    title: "ID",
    headerClassName: "w-[200px]",
    render: (value, item) => (
      <Typography className="text-admin_text text-sm font-mono">
        {getItemId(item)}
      </Typography>
    ),
  },
]

// Configuration store for renders
let currentMatch = null

// Initialize/update match configuration
const initializeMatch = (match) => {
  currentMatch = match
}

// Type-specific column configurations
const typeSpecificColumns = {
  File: [
    {
      id: "path",
      title: "Path",
      headerClassName: "w-[300px]",
      render: (value, item) => <PathCell path={item.path} />,
    },
    {
      id: "size",
      title: "Size",
      headerClassName: "w-[100px]",
      render: (value, item) => (
        <Typography className="text-admin_text">
          {item.formattedSize || formatFileSize(item.size)}
        </Typography>
      ),
    },
    {
      id: "timeCreated",
      title: "Created",
      headerClassName: "w-[200px]",
      render: (value, item) => (
        <Typography className="text-admin_text">
          {formatDate(item.timeCreated)}
        </Typography>
      ),
    },
  ],

  CMSContent: [
    ...baseColumns,
    {
      id: "createdAt",
      title: "Created",
      headerClassName: "w-[120px]",
      render: (value, item) => (
        <Typography className="text-admin_text text-sm">
          {formatDate(item.createdAt)}
        </Typography>
      ),
    },
    {
      id: "fields",
      title: "Fields",
      headerClassName: "w-[80px]",
      render: (value, item) => (
        <Typography className="text-admin_text text-sm text-center">
          {item.content?.fields?.length || 0}
        </Typography>
      ),
    },
    {
      id: "status",
      title: "Status",
      headerClassName: "w-[80px]",
      render: (value, item) => (
        <div className="flex items-center">
          <div className={`w-2 h-2 rounded-full mr-2 ${item.deleted ? 'bg-admin_error' : 'bg-admin_success'}`} />
          <Typography className="text-admin_text text-sm">
            {item.deleted ? "Deleted" : "Active"}
          </Typography>
        </div>
      ),
    },
  ],

  CMSType: [
    ...baseColumns,
    {
      id: "createdAt",
      title: "Created",
      headerClassName: "w-[120px]",
      render: (value, item) => (
        <Typography className="text-admin_text text-sm">
          {formatDate(item.createdAt)}
        </Typography>
      ),
    },
    {
      id: "fields",
      title: "Fields",
      headerClassName: "w-[80px]",
      render: (value, item) => (
        <Typography className="text-admin_text text-sm text-center">
          {item.content?.fields?.length || 0}
        </Typography>
      ),
    },
    {
      id: "status",
      title: "Status",
      headerClassName: "w-[80px]",
      render: (value, item) => (
        <div className="flex items-center">
          <div className={`w-2 h-2 rounded-full mr-2 ${item.deleted ? 'bg-admin_error' : 'bg-admin_success'}`} />
          <Typography className="text-admin_text text-sm">
            {item.deleted ? "Deleted" : "Active"}
          </Typography>
        </div>
      ),
    },
  ],

  StorePrice: [
    ...baseColumns,
    {
      id: "amount",
      title: "Amount",
      headerClassName: "w-[120px]",
      render: (value, item) => (
        <Typography className="text-admin_text">
          {formatCurrency(item.amount)}
        </Typography>
      ),
    },
    {
      id: "currency",
      title: "Currency",
      headerClassName: "w-[100px]",
      render: (value, item) => (
        <Typography className="text-admin_text">
          {item.currency || "USD"}
        </Typography>
      ),
    },
  ],

  StoreProduct: [
    ...baseColumns,
    {
      id: "sku",
      title: "SKU",
      headerClassName: "w-[150px]",
      render: (value, item) => (
        <Typography className="text-admin_text">
          {item.sku || "No SKU"}
        </Typography>
      ),
    },
    {
      id: "price",
      title: "Base Price",
      headerClassName: "w-[120px]",
      render: (value, item) => (
        <Typography className="text-admin_text">
          {formatCurrency(item.basePrice)}
        </Typography>
      ),
    },
  ],

  User: [
    ...baseColumns,
    {
      id: "email",
      title: "Email",
      headerClassName: "w-[200px]",
      render: (value, item) => (
        <Typography className="text-admin_text">
          {item.email || "No email"}
        </Typography>
      ),
    },
    {
      id: "role",
      title: "Role",
      headerClassName: "w-[100px]",
      render: (value, item) => (
        <Typography className="text-admin_text">
          {item.role || "User"}
        </Typography>
      ),
    },
  ],

  UserDevice: [
    ...baseColumns,
    {
      id: "deviceType",
      title: "Device Type",
      headerClassName: "w-[150px]",
      render: (value, item) => (
        <Typography className="text-admin_text">
          {item.deviceType || "Unknown"}
        </Typography>
      ),
    },
    {
      id: "lastSeen",
      title: "Last Seen",
      headerClassName: "w-[200px]",
      render: (value, item) => (
        <Typography className="text-admin_text">
          {formatDate(item.lastSeen)}
        </Typography>
      ),
    },
  ],

  Localization: [
    ...baseColumns,
    {
      id: "locale",
      title: "Locale",
      headerClassName: "w-[100px]",
      render: (value, item) => (
        <Typography className="text-admin_text">
          {item.locale || "en"}
        </Typography>
      ),
    },
    {
      id: "translations",
      title: "Translations",
      headerClassName: "w-[150px]",
      render: (value, item) => (
        <Typography className="text-admin_text">
          {Object.keys(item.translations || {}).length} keys
        </Typography>
      ),
    },
  ],
}

// Helper functions
const formatFileSize = (bytes) => {
  if (!bytes) return "0 B"
  const sizes = ["B", "KB", "MB", "GB"]
  const i = Math.floor(Math.log(bytes) / Math.log(1024))
  return `${(bytes / Math.pow(1024, i)).toFixed(2)} ${sizes[i]}`
}

const formatCurrency = (amount) => {
  if (!amount) return "$0.00"
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
  }).format(amount)
}

const formatDate = (date) => {
  if (!date) return "Never"
  const dateObj = new Date(date)
  const now = new Date()
  const diffMs = now - dateObj
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24))
  
  if (diffDays === 0) {
    return "Today"
  } else if (diffDays === 1) {
    return "Yesterday"
  } else if (diffDays < 7) {
    return `${diffDays} days ago`
  } else {
    return dateObj.toLocaleDateString()
  }
}

// Main export function
export const rendersForType = (type, match) => {
  initializeMatch(match)
  return typeSpecificColumns[type] || baseColumns
}

// Export base columns for reuse if needed
export const getBaseColumns = () => baseColumns
